import founderCutout from "@/assets/images/founder-transparent-cropped.webp";
import { Youtube, Instagram, Music, Music2, Music4, Facebook, Linkedin } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import PlatformButton from "@/components/PlatformButton";
import { FOUNDER_ARTIST_LINKS } from "@/data/socialLinks";

const ICONS = {
  YouTube: Youtube,
  "YouTube Music": Music4,
  "Amazon Music": Music,
  Spotify: Music2,
  "Apple Music": Music,
  Instagram: Instagram,
  Facebook: Facebook,
  LinkedIn: Linkedin,
};

const FOUNDER_VALUES = [
  { title: "Think Deeply", copy: "Look beneath the obvious before deciding what deserves to be built." },
  { title: "Create Intentionally", copy: "Beauty is most powerful when it carries meaning and purpose." },
  { title: "Build Enduringly", copy: "Choose long-term value, substance and emotional resonance over temporary attention." },
  { title: "Remain Human", copy: "No opportunity is more valuable than the people affected by it." },
];

const Founder = () => {
  return (
    <div data-testid="page-founder">
      <SEO
        title="Founder"
        description="Gopi Gadhvi is the Founder & CEO of Gopi Gadhvi Group, a multidisciplinary entrepreneur based in Ahmedabad, India."
        path="/founder"
      />

      {/* Eyebrow + H1 now live directly inside the two-column grid (rather
          than a separate PageHero section) so the founder image's top
          edge begins parallel with the heading, not the biography — and
          the title-to-body gap collapses into one continuous editorial
          introduction. */}
      <section className="relative page-shell pt-32 md:pt-36 lg:pt-40 pb-16 md:pb-20 lg:pb-24">
        <div className="grid lg:grid-cols-[1fr_460px] gap-14 items-stretch">
          <div>
            <div className="max-w-3xl mb-8">
              <span className="eyebrow block mb-5" data-testid="page-hero-eyebrow">The Founder</span>
              <Reveal as="h1" className="text-h1" delay={80}>
                <span data-testid="page-hero-heading">The Heart Behind the Universe</span>
              </Reveal>
            </div>

            <Reveal className="space-y-6 mb-12" delay={100}>
              <p className="text-editorial text-justify">
                Gopi Gadhvi is the Founder &amp; CEO of Gopi Gadhvi Group, a multidisciplinary
                entrepreneur based in Ahmedabad, India.
              </p>
              <p className="text-editorial text-justify">
                Her work spans private real estate advisory, interior design, digital creativity,
                music, publishing and personal transformation. Though these fields may appear
                separate, each venture begins with the same questions: What can be created with
                greater meaning? How can beauty become useful? How can a business leave people
                better than it found them?
              </p>
              <p className="text-editorial text-justify">
                Gopi&rsquo;s approach is both intuitive and exacting. She is drawn to ideas with
                emotional depth, long-term potential and the power to change how people experience
                their lives.
              </p>
              <p className="text-editorial text-justify">
                Gopi Gadhvi Group was created not to force every ambition into one company, but to
                give each expression its own identity while allowing them all to grow from one
                enduring philosophy.
              </p>
            </Reveal>

            {/* Founder quote — editorial centrepiece. Same script family
                (Rouge Script) and the same rich H1 gold as the homepage
                hero tagline. Oversized, low-opacity decorative quote
                marks frame the text without ever obstructing it. */}
            <Reveal delay={130} className="mb-4">
              <div className="founder-quote-wrap" data-testid="founder-quote-block">
                <span className="founder-quote-mark founder-quote-mark-open" aria-hidden="true">&ldquo;</span>
                <p className="founder-quote-text" data-testid="founder-quote-text">
                  &ldquo;I did not create one business and ask my dreams to fit inside it. I
                  created a group spacious enough to hold every world I am here to build.&rdquo;
                </p>
                <p className="founder-quote-attribution" data-testid="founder-quote-attribution">Gopi Gadhvi</p>
                <span className="founder-quote-mark founder-quote-mark-close" aria-hidden="true">&rdquo;</span>
              </div>
            </Reveal>

            <Reveal delay={180}>
              <span className="eyebrow block mb-6">Founder Values</span>
              <div className="grid sm:grid-cols-2 gap-5">
                {FOUNDER_VALUES.map((v, i) => (
                  <div key={v.title} className="neu-surface neu-surface-hover p-6" data-testid={`founder-value-${i}`}>
                    <p className="manifesto-number mb-2" style={{ color: "var(--gg-logo-gold)" }}>{String(i + 1).padStart(2, "0")}</p>
                    <p className="text-h4 mb-2" style={{ fontSize: "1.15rem", color: "var(--gg-logo-gold)" }}>{v.title}</p>
                    <p className="text-body">{v.copy}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={120} className="relative lg:h-full">
            <img
              src={founderCutout}
              alt="Gopi Gadhvi, Founder & CEO of Gopi Gadhvi Group"
              className="w-full lg:h-full object-contain object-top mx-auto max-w-[420px] lg:max-w-none"
              style={{ filter: "drop-shadow(0 10px 34px rgba(0,0,0,0.5))" }}
              data-testid="founder-photo"
            />
          </Reveal>
        </div>
      </section>

      <section className="relative py-14 md:py-20 lg:py-24" style={{ background: "var(--gg-midnight-black)" }}>
        <div className="page-shell text-center">
          <Reveal className="max-w-2xl mx-auto mb-12">
            <span className="eyebrow block mb-5">As An Artist</span>
            <h2 className="text-h2 mb-6">Another Expression of the Same Inner World</h2>
            <p className="text-editorial mx-auto">
              Alongside her entrepreneurial work, Gopi Gadhvi creates music and artistic
              experiences shaped by emotion, imagination and inner reflection.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5 max-w-4xl mx-auto">
            {FOUNDER_ARTIST_LINKS.map(({ label, url }) => (
              <PlatformButton
                key={label}
                href={url}
                label={label}
                icon={ICONS[label] || Music}
                testId={`founder-artist-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Founder;
